import * as React from "react";
import Toggle from "react-toggle";
import { Dropdown } from "react-bootstrap";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getChangedPeopleSchedulingParamsCount } from "@/lib/shifts";
import { classNames } from "@/lib/util";

import type { IPeopleSchedulingParams } from "@/lib/backend/manufacturing2.generated.types";

import "./PeopleSchedulingSettings.scss";

export const PeopleSchedulingSettings = (props: {
    className?: string;
    disabled?: boolean;
    scheduling_params: IPeopleSchedulingParams;
    default_scheduling_params?: IPeopleSchedulingParams;
    onSchedulingParamsChange: (params: IPeopleSchedulingParams) => void;
}) => {
    const {
        className,
        disabled = false,
        scheduling_params,
        default_scheduling_params,
        onSchedulingParamsChange,
    } = props;

    const handleSettingsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    const handleOneCompetencePerShiftChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSchedulingParamsChange({
            ...scheduling_params,
            allow_one_competence_per_shift: event.currentTarget.checked,
        });
    };

    const handleOnePersonPerLineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSchedulingParamsChange({
            ...scheduling_params,
            allow_one_person_per_single_person_line: event.currentTarget.checked,
        });
    };

    const handleAllowOverworkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSchedulingParamsChange({
            ...scheduling_params,
            allow_overwork: event.currentTarget.checked,
        });
    };

    const handlePreferFullyScheduledPeopleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSchedulingParamsChange({
            ...scheduling_params,
            prefer_fully_scheduled_people: event.currentTarget.checked,
        });
    };

    const handleConsiderDwellTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSchedulingParamsChange({
            ...scheduling_params,
            consider_dwell_time: event.currentTarget.checked,
        });
    };

    const handleConsiderPartialRetirementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onSchedulingParamsChange({
            ...scheduling_params,
            consider_partial_retirement: event.currentTarget.checked,
        });
    };

    const changed_params_count = getChangedPeopleSchedulingParamsCount(scheduling_params, default_scheduling_params);

    return (
        <div className={classNames("people-scheduling-settings", className)}>
            <Dropdown>
                <Dropdown.Toggle
                    as="button"
                    className="btn btn-short btn-outline-secondary position-relative"
                    disabled={disabled}
                >
                    <i className="fas fa-cog" />
                </Dropdown.Toggle>
                <Dropdown.Menu align="right">
                    <div className="py-2" onClick={handleSettingsMenuClick}>
                        <div className="form-group px-3 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate(
                                        "ShiftAssignmentWeekPool.allow_one_competence_per_shift_per_person",
                                        "Allow a person to work only on lines with the same competence on the same shift"
                                    )}
                                </span>
                                <Toggle
                                    checked={scheduling_params.allow_one_competence_per_shift}
                                    disabled={disabled}
                                    onChange={handleOneCompetencePerShiftChange}
                                />
                            </label>
                        </div>
                        <div className="dropdown-divider" />
                        <div className="form-group px-3 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate(
                                        "ShiftAssignmentWeekPool.allow_one_person_per_line",
                                        "Allow only one person per line when competence factor is less than or equal to 1.0"
                                    )}
                                </span>
                                <Toggle
                                    checked={scheduling_params.allow_one_person_per_single_person_line}
                                    disabled={disabled}
                                    onChange={handleOnePersonPerLineChange}
                                />
                            </label>
                        </div>
                        <div className="dropdown-divider" />
                        <div className="form-group px-3 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate(
                                        "ShiftAssignmentWeekPool.allow_person_to_exceed_max_work_hours",
                                        "Allow a person to exceed the maximum work hours per shift"
                                    )}
                                </span>
                                <Toggle
                                    checked={scheduling_params.allow_overwork}
                                    disabled={disabled}
                                    onChange={handleAllowOverworkChange}
                                />
                            </label>
                        </div>
                        <div className="dropdown-divider" />
                        <div className="form-group px-3 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate(
                                        "ShiftAssignmentWeekPool.prefer_fully_scheduled_people",
                                        "Prefer fully scheduled people"
                                    )}
                                </span>
                                <Toggle
                                    checked={scheduling_params.prefer_fully_scheduled_people}
                                    disabled={disabled}
                                    onChange={handlePreferFullyScheduledPeopleChange}
                                />
                            </label>
                        </div>
                        <div className="dropdown-divider" />
                        <div className="form-group px-3 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate("ShiftAssignmentWeekPool.consider_dwell_time", "Consider dwell time")}
                                </span>
                                <Toggle
                                    checked={scheduling_params.consider_dwell_time}
                                    disabled={disabled}
                                    onChange={handleConsiderDwellTimeChange}
                                />
                            </label>
                        </div>
                        <div className="dropdown-divider" />
                        <div className="form-group px-3 mb-0">
                            <label className="d-flex align-items-center mr-auto mb-0">
                                <span className="pr-2 mr-auto">
                                    {translate(
                                        "ShiftAssignmentWeekPool.consider_partial_retirement",
                                        "Consider partial retirement"
                                    )}
                                </span>
                                <Toggle
                                    checked={scheduling_params.consider_partial_retirement}
                                    disabled={disabled}
                                    onChange={handleConsiderPartialRetirementChange}
                                />
                            </label>
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
            {changed_params_count > 0 ? <span className="badge badge-danger">{changed_params_count}</span> : null}
        </div>
    );
};
